import { useEffect, useState } from 'react';

import { Itimezone } from '../../api/types';
import { GeneralBtn, SingleSelection } from '../../components';
import { Ilocale } from '../../components/i18nProvider';
import { Box, Button, Container, SelectChangeEvent } from '../../components/MuiGenerals';
import { useAppDispatch, useAppSelector } from '../../reducer/hooks';
import { selectProfile, setAccountSetting } from '../../reducer/profileSlice';
import { enumMapping } from '../../utils';
import { enumLanguage, enumPortalUserYubiKeyStatus, enumTimezone } from '../../utils/constant';
import { useZusTranslatePrefixStore } from '../../zustand/store';
import LogoutButton from '../../components/Button/LogoutButton';
import { selectIsMobileView } from '../../reducer/mediaSlice';
import { useTranslation } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ArrowDown } from '../../assets/icons/arrowDown.svg';
import { useTheme } from '@mui/material';

const translatePrefix = 'accountSetting';
const Enum = {
  timezones: enumMapping(enumTimezone),
  yubikeyStatus: enumMapping(enumPortalUserYubiKeyStatus),
};

interface Ifields {
  email: string;
  timezone: Itimezone;
  lang: keyof typeof enumLanguage;
}
const initFields = {
  email: '',
  yubikey_status: enumPortalUserYubiKeyStatus.Unbound,
  timezone: enumTimezone.timezone8,
  lang: enumLanguage.en,
} as const;
const sx = {
  Box: {
    display: 'grid',
    gridTemplateColumns: 'minmax(5rem, max-content) minmax(10rem, max-content)',
    lineHeight: '3rem',
    columnGap: '1rem',
    marginBottom: '1rem',
  },
  EmailPasswordBox: {
    display: 'grid',
    gridTemplateColumns: 'minmax(6rem, max-content) minmax(10rem, max-content)',
    lineHeight: '3rem',
    columnGap: '0.5rem',
    marginBottom: '1rem',
  },
};
export default function AccountSetting() {
  const dispatch = useAppDispatch();
  const { setTranslatePrefix } = useZusTranslatePrefixStore();
  const { email, timezone, lang } = useAppSelector(selectProfile);

  const [fields, setFields] = useState<Ifields>({
    ...initFields,
    email,
    timezone,
    lang,
  });

  const [inView, setInView] = useState(true);

  const { t, te, changeLanguage, currentLanguage } = useTranslation(translatePrefix);
  const redirect = useNavigate();

  const isMobile = useAppSelector(selectIsMobileView);

  const onSelectChange = (type: string) => (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setFields((fields: any) => ({ ...fields, [type]: value }));
  };

  const onSave = async () => {
    // const res = await APIs.General.Profile.upsertUserProfile({
    //   lang: fields.lang,
    //   timezone: fields.timezone,
    // });
    // if (!res) return;
    changeLanguage(fields.lang);
    dispatch(setAccountSetting({ lang: fields.lang, timezone: fields.timezone }));
    setInView(true);
  };

  const onCancel = () => {
    setFields({ ...fields, email, timezone, lang });
    setInView(true);
  };

  useEffect(() => setTranslatePrefix(translatePrefix), []);

  useEffect(() => {
    setFields({ ...fields, lang: currentLanguage as keyof typeof enumLanguage });
  }, [currentLanguage]);

  return (
    <>
      <Box style={{ padding: isMobile ? '0 32px' : '0' }} sx={{ marginBottom: '10px' }}>
        <Container disableGutters maxWidth={false}>
          <Box sx={sx.EmailPasswordBox}>
            <span>{t('email')}</span>
            <span>{email}</span>
            {/* <span>{t('password')}</span>
            <span>
              <Button
                onClick={() => {
                  redirect('/changePassword');
                }}
                variant="contained"
                color="secondary"
              >
                {t('changePassword')}
              </Button>
            </span> */}
          </Box>
        </Container>
      </Box>
      <Box style={{ padding: isMobile ? '0 32px' : '0' }}>
        <Container disableGutters maxWidth={false}>
          <Box sx={sx.Box}>
            {inView && (
              <>
                <span>{t('timezone')}</span>
                <span>GMT{te(Enum.timezones[timezone])}</span>
                {/* <span>{t('language')}</span>
                <span>{te(currentLanguage as any)}</span> */}
              </>
            )}
            {!inView && (
              <>
                <span>{t('timezone')}</span>
                <SingleSelection
                  value={fields.timezone}
                  onChange={onSelectChange('timezone')}
                  enumData={enumTimezone}
                  arrayFn={(arr) => arr.sort((a, b) => Number(a[1]) - Number(b[1]))}
                  nameFn={(name) => `GMT${te(name as any)}`}
                  clearSelect={() => {}}
                />
                {/* <span>{t('language')}</span>
                <SingleSelection
                  value={fields.lang}
                  onChange={onSelectChange('lang')}
                  enumData={enumLanguage}
                  clearSelect={() => {}}
                /> */}
              </>
            )}
          </Box>
          {inView && <GeneralBtn onClick={() => setInView(false)} label="edit" />}
          {!inView && <GeneralBtn onClick={onSave} label="confirm" color={'primary'} />}
          {!inView && (
            <GeneralBtn
              onClick={onCancel}
              label="cancel"
              rest={{ style: { marginLeft: '40px' } }}
            />
          )}
        </Container>
      </Box>

      <div style={{ padding: isMobile ? '32px' : '32px 0' }}>
        <LogoutButton />
      </div>
    </>
  );
}

export function LanguageSelection() {
  const profile = useAppSelector(selectProfile);
  const timezone = profile.timezone;
  const dispatch = useAppDispatch();

  const { currentLanguage, changeLanguage } = useTranslation();

  const onSelectChange = (type: string) => async (e: SelectChangeEvent<string>) => {
    if (type !== 'lang') return;
    const lang = e.target.value as Ilocale;

    changeLanguage(lang);
    dispatch(setAccountSetting({ lang, timezone }));
  };
  const propSx = {
    formControl: {
      '& .MuiInputBase-root': {
        height: '24px',
        borderRadius: '.5rem',
      },
    },
    select: {
      '&.MuiInputBase-root': {
        '> svg': {
          top: '50%',
          width: '6px',
          height: '6px',
          right: '16px',
          transform: 'translateY(-50%)',
        },
      },
    },
  };

  return (
    <SingleSelection
      value={currentLanguage}
      onChange={onSelectChange('lang')}
      enumData={enumLanguage}
      nameFn={(name) => {
        if (name === 'en') return 'English';
        return '繁中';
      }}
      clearSelect={() => {}}
      sx={propSx}
      className={'languageSelection'}
      iconComponent={ArrowDown}
    />
  );
}
